/**
 * 834CH のデータ構造。
 * フロントエンド・Worker・バッチスクリプトで共有する。
 * 要件定義 3.1 / 5 / 8 / 9 章に対応。
 */

/** 動画の分類（要件定義 5 章） */
export const VIDEO_TYPES = [
  'music-video',
  'live',
  'interview',
  'talk',
  'radio',
  'documentary',
  'other',
] as const

export type VideoType = (typeof VIDEO_TYPES)[number]

/** 許可チャンネルの種別（要件定義 4.2） */
export const CHANNEL_CATEGORIES = [
  'artist',
  'label',
  'tv',
  'radio',
  'festival',
  'media',
  'other',
] as const

export type ChannelCategory = (typeof CHANNEL_CATEGORIES)[number]

/** 番組編成の対象となる 1 本の動画 */
export type Video = {
  /** YouTube の video ID */
  id: string
  title: string
  channelId: string
  channelName: string
  /** 尺（秒） */
  duration: number
  /** ISO8601。YouTube の publishedAt をそのまま保持する */
  publishedAt: string
  type: VideoType
  tags: string[]
  description: string
  /** false のものは番組表に載せない */
  enabled: boolean
  /** AI 解析済みかどうか。未解析の動画を絞り込むために使う */
  analyzedAt?: string
  /** 再生できなかった回数（要件定義 13 章のフォールバック用） */
  playbackErrorCount?: number
}

/** KV に保存する videos.json の中身 */
export type VideoLibrary = {
  updatedAt: string
  videos: Video[]
}

/** 番組表上の 1 番組（要件定義 3.1） */
export type Program = {
  videoId: string
  /** ISO8601 (+09:00) */
  startAt: string
  /** ISO8601 (+09:00) */
  endAt: string
}

/** KV に保存する schedule.json の中身（要件定義 8 章） */
export type Schedule = {
  /** 週の開始日 (JST の月曜日, YYYY-MM-DD) */
  week: string
  programs: Program[]
  /**
   * この番組表を組んだときの編成方針。
   * policy.json は常に「次に作る週」のものへ上書きされるため、
   * 放送中の週の方針を番組表から引けるようにここへ写しておく。
   */
  slots?: PolicySlot[]
  /** 生成時刻。運用時の追跡用で、番組編成には影響しない */
  generatedAt?: string
}

/** 許可チャンネル（要件定義 4.2） */
export type Channel = {
  channelId: string
  name: string
  /** YouTube のハンドル（@sakanaction）。channelId の確認・再解決に使う */
  handle?: string
  category: ChannelCategory
  enabled: boolean
  note?: string
}

/** KV に保存する channels.json の中身 */
export type ChannelList = {
  channels: Channel[]
}

/** KV に保存する blockedVideos.json の中身（要件定義 9 章） */
export type BlockedVideos = {
  videoIds: string[]
  updatedAt?: string
}

/** 曜日。JST の月曜はじまり */
export const WEEKDAYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const

export type Weekday = (typeof WEEKDAYS)[number]

/**
 * 編成方針の 1 枠（要件定義 7.2）。
 * AI が決めるのはこの「どの時間帯にどんな動画を流すか」という意味的な判断だけで、
 * 実際にどの動画を並べるかはプログラムが決める。
 */
export type PolicySlot = {
  day: Weekday
  /** JST の開始時（0-23） */
  startHour: number
  /** JST の終了時（1-24）。startHour より大きいこと */
  endHour: number
  /** 番組枠の名前（例: 深夜のロングインタビュー） */
  label: string
  /** 優先する動画種別。空なら種別を問わない */
  types: VideoType[]
  /** 優先するタグ。空ならタグを問わない */
  tags: string[]
}

/** KV に保存する policy.json の中身 */
export type ProgrammingPolicy = {
  /** 週の開始日 (JST の月曜日, YYYY-MM-DD) */
  week: string
  slots: PolicySlot[]
  generatedAt?: string
  /** AI 生成に失敗して既定の方針を使ったかどうか */
  fallback?: boolean
}

/** KV のキー名 */
export const KV_KEYS = {
  videos: 'videos.json',
  schedule: 'schedule.json',
  /** 翌週分の番組表。週が変わったら schedule.json に昇格する */
  scheduleNext: 'schedule.next.json',
  channels: 'channels.json',
  blockedVideos: 'blockedVideos.json',
  policy: 'policy.json',
  /** コメントの受け付け設定（全体ミュート / NG ワード） */
  moderation: 'moderation.json',
} as const

export type KvKey = (typeof KV_KEYS)[keyof typeof KV_KEYS]
